'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import { LANG_COOKIE, DEFAULT_LANG, isRtlLang } from './languageConstants';
import { setRuntimeTranslations } from '@client/i18n/runtimeTranslation';
import { setRuntimeLocale } from '@client/i18n/runtimeLocale';

const LS_KEY = 'rra_lang';

export { LANG_COOKIE, DEFAULT_LANG, isRtlLang };

function writeLangCookie(code: string) {
  if (typeof document === 'undefined') return;
  // 1 year, root path, lax — readable on the server for SSR.
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${LANG_COOKIE}=${encodeURIComponent(code)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export interface Language {
  code: string;
  name: string;
  native_name: string;
  flag_emoji: string;
  is_rtl: boolean;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
}

/**
 * The translate function's real shape.
 *
 * Components that take `t` as a prop used to re-declare it without the `vars`
 * argument, so an interpolated string failed to compile in exactly the places
 * that needed one. Import this instead of writing the signature out.
 */
export type Translate = (
  key: string,
  fallback?: string,
  vars?: Record<string, string | number>,
) => string;

interface LanguageContextValue {
  currentLang: string;
  isRTL: boolean;
  dir: 'ltr' | 'rtl';
  translations: Record<string, string>;
  loading: boolean;
  setLang: (code: string) => void;
  t: (key: string, fallback?: string, vars?: Record<string, string | number>) => string;
  activeLanguages: Language[];
  syncUserLanguage: (preferredLang: string) => void;
  bustCache: (langCode: string) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  currentLang: DEFAULT_LANG,
  isRTL: false,
  dir: 'ltr',
  translations: {},
  loading: false,
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  activeLanguages: [],
  syncUserLanguage: () => {},
  bustCache: () => {},
});

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useTranslation() {
  const { t, currentLang, isRTL, dir } = useContext(LanguageContext);
  return { t, currentLang, isRTL, dir };
}

export function LanguageProvider({
  children,
  initialLang,
  initialTranslations,
  translationsPartial = false,
}: {
  children: React.ReactNode;
  initialLang?: string;
  initialTranslations?: Record<string, string>;
  /**
   * True when `initialTranslations` holds only the marketing namespaces rather
   * than the whole catalogue (see `PUBLIC_TRANSLATION_PREFIXES`). The subset
   * covers everything the marketing pages paint, so there is still no
   * English → Turkish flash, but the per-language cache must NOT be pre-warmed
   * with it — otherwise the mount-time fetch is a cache hit and the remaining
   * keys never arrive, leaving client-side navigation into the dashboard
   * rendering English fallbacks.
   */
  translationsPartial?: boolean;
}) {
  // The initial language must come from a source available on both server
  // and first client render (a cookie injected by the server) so the SSR
  // markup matches what React produces on hydration. We deliberately do
  // NOT read localStorage to seed initial state — that would diverge from
  // the SSR pass and trigger a hydration mismatch.
  const [currentLang, setCurrentLang] = useState<string>(initialLang || DEFAULT_LANG);
  // Seed translations from the server-rendered payload so the very first
  // paint (SSR + hydration) is already translated. Without this the map is
  // empty until the client fetch resolves, so every t() falls back to its
  // English literal and the page visibly flashes English → Turkish on load.
  const [translations, setTranslations] = useState<Record<string, string>>(
    initialTranslations ?? {},
  );
  const [activeLanguages, setActiveLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  // Pre-warm the per-language cache with the SSR payload so the mount-time
  // fetchTranslations(currentLang) is a cache hit and never hits the network
  // (and never re-renders with different data → no flash).
  // A partial payload is deliberately left out of the cache so the mount-time
  // fetch misses and pulls the complete catalogue in the background.
  const cacheRef = useRef<Record<string, Record<string, string>>>(
    initialTranslations && !translationsPartial && (initialLang || DEFAULT_LANG)
      ? { [initialLang || DEFAULT_LANG]: initialTranslations }
      : {},
  );

  useEffect(() => {
    // Migrate any legacy localStorage value into the cookie so subsequent
    // requests render the right language on the server too.
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(LS_KEY);
      if (saved && saved !== currentLang) {
        writeLangCookie(saved);
        setCurrentLang(saved);
      } else if (currentLang) {
        writeLangCookie(currentLang);
      }
    }
    fetchActiveLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTranslations(currentLang);
  }, [currentLang]);

  useEffect(() => {
    setRuntimeTranslations(translations);
  }, [translations]);

  // Seeded during the first client render — before any effect — so helpers
  // that format dates while the page mounts already use the right locale.
  // Skipped on the server: the module is shared across requests there.
  if (typeof window !== 'undefined') setRuntimeLocale(currentLang);
  useEffect(() => {
    setRuntimeLocale(currentLang);
  }, [currentLang]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const meta = activeLanguages.find(l => l.code === currentLang);
    const rtl = meta ? meta.is_rtl : isRtlLang(currentLang);
    document.documentElement.lang = currentLang;
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    if (rtl) {
      document.body.classList.add('rtl-mode');
    } else {
      document.body.classList.remove('rtl-mode');
    }
  }, [currentLang, activeLanguages]);

  async function fetchActiveLanguages() {
    try {
      const res = await fetch('/api/public/languages');
      if (res.ok) {
        const data = await res.json() as { languages: Language[] };
        setActiveLanguages(data.languages || []);
      }
    } catch {
      setActiveLanguages([]);
    }
  }

  async function fetchTranslations(lang: string) {
    if (cacheRef.current[lang]) {
      setTranslations(cacheRef.current[lang]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/public/translations?lang=${encodeURIComponent(lang)}`);
      if (res.ok) {
        const data = await res.json() as Record<string, string>;
        cacheRef.current[lang] = data || {};
        setTranslations(data || {});
      }
    } catch {
      // Keep whatever is already rendered. This used to clear the map, which
      // was harmless when the server had sent the full catalogue and the cache
      // made this a no-op — but now that marketing pages are seeded with a
      // partial payload and deliberately miss the cache, wiping it on a failed
      // background fetch would flip a fully translated page to English
      // fallbacks. A stale-but-correct map always beats an empty one.
    } finally {
      setLoading(false);
    }
  }

  const syncUserLanguage = useCallback((preferredLang: string) => {
    if (!preferredLang) return;
    setCurrentLang(prev => {
      if (prev === preferredLang) return prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(LS_KEY, preferredLang);
        writeLangCookie(preferredLang);
      }
      return preferredLang;
    });
  }, []);

  const setLang = useCallback((code: string) => {
    setCurrentLang(code);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LS_KEY, code);
      writeLangCookie(code);
    }
    fetch('/api/auth/me/language', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang: code }),
    }).catch(() => {});
  }, []);

  const bustCache = useCallback((langCode: string) => {
    delete cacheRef.current[langCode];
    setCurrentLang(prev => {
      if (prev === langCode) {
        void fetchTranslations(langCode);
      }
      return prev;
    });
  }, []);

  const t = useCallback((key: string, fallback?: string, vars?: Record<string, string | number>): string => {
    let result = translations[key] ?? fallback ?? key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        let val = String(v);
        // In Turkish, counts/quantities do not take plural suffixes (e.g. '3 ürün' instead of '3 ürüns')
        if (currentLang === 'tr' && (k === 'plural' || k === 's')) {
          val = '';
        }
        result = result.replace(new RegExp(`\\{\\{${k}\\}\\}|\\{${k}\\}`, 'g'), val);
      });
    }
    return result;
  }, [translations, currentLang]);

  const currentLangMeta = activeLanguages.find(l => l.code === currentLang);
  const isRTL = currentLangMeta ? currentLangMeta.is_rtl : isRtlLang(currentLang);

  return (
    <LanguageContext.Provider value={{
      currentLang,
      isRTL,
      dir: isRTL ? 'rtl' : 'ltr',
      translations,
      loading,
      setLang,
      t,
      activeLanguages,
      syncUserLanguage,
      bustCache,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}
